import { IMasterTableData } from './models/master-table';
import { DocumentType } from './models/document';
import { LookupType } from './models/lookup';

import { Operator } from '../utils/matchers/ICondition';
import { OptionProps } from '../components/forms';

export type Optional<T> = T | undefined;

export type SelectOption = OptionProps;

//-------------------------------------------
// Form Type
//-------------------------------------------

export enum FormType {
  APPLICATION = 'application',
  QUIZ = 'quiz',
}

//-------------------------------------------
// Form Component Type
//-------------------------------------------

export enum FormComponentType {
  Panel = 'panel',
  Label = 'label',
  Text = 'text',
  Textarea = 'textarea',
  Dropdown = 'dropdown',
  Checkbox = 'checkbox',
  Checkboxgroup = 'checkboxgroup',
  Number = 'number',
  Radiogroup = 'radiogroup',
  Date = 'date',
  Address = 'address',
  ProfileInfo = 'profileInfo',
  RecommenderInfo = 'recommenderInfo',
  CounselorInfo = 'counselorInfo',
  PhoneNumber = 'phone',
  Grade = 'grade',
  RichTextEditor = 'rte',
  DateTime = 'datetime',
  AutoComplete = 'autocomplete',
  LinkToPage = 'linkToPage',
  Location = 'location',
  SSN = 'ssn',
  MultiSelectPhotos = 'multiSelectPhotos',
  FileUpload = 'fileUpload',
  DocumentUploadPanel = 'documentUploadPanel',
  DocumentTypeMetadataTitle = 'documentTypeMetadataTitle',
  DocumentUpload = 'documentUpload',
  ReaderQuiz = 'readerQuiz',
  CoachQuiz = 'coachQuiz',
  InterviewerQuiz = 'interviewerQuiz',
  MentorQuiz = 'mentorQuiz',
  Hidden = 'hidden',
  DragNDropOption = 'dragNDropOption',
  DragNDropOptionPanel = 'dragNDropOptionPanel',
  FERPAStatement = 'ferpa',
  FinancialPortal = 'financialPortal',
  PaymentCampaign = 'paymentCampaign',
}

export enum FormPanelComponentType {
  Panel = 'panel',
  Address = 'address',
  ProfileInfo = 'profileInfo',
  RecommenderInfo = 'recommenderInfo',
  DocumentUploadPanel = 'documentUploadPanel',
  CounselorInfo = 'counselorInfo',
  DateTime = 'datetime',
  Location = 'location',
  ReaderQuiz = 'readerQuiz',
  CoachQuiz = 'coachQuiz',
  InterviewerQuiz = 'interviewerQuiz',
  MentorQuiz = 'mentorQuiz',
  DragNDropOptionPanel = 'dragNDropOptionPanel',
  FERPAStatement = 'ferpa',
  FinancialPortal = 'financialPortal',
}

// value used as a label in form builder `Value Settings`
export enum NumberType {
  Integer = 'integer',
  Decimal = 'decimal',
}

export enum GradeType {
  Letter = 'letter',
  Number = 'number',
}

export type InputType = 'input' | 'password';

export type ValueType = NumberType | GradeType;

export interface IValidatableSchema {
  elements?: IValidatableSchema[]; // panel only
  name: string;
  title?: string;
  validation?: IValidationSchema;
  repeatable?: boolean;
  type?: FormComponentType;
}

export interface OrCondition {
  or?: IVisibleSchema[];
}

export interface ICampaignDate {
  name: string; // Campaign name
  date: string; // ISO date string - End date for this campaign (transition point). Last campaign ends at stageEndDate.
  cost: number; // Cost in cents (for Stripe)
}

/** Validation criteria for document/image uploads (e.g. Headshot). Resolved from centralized defaults + admin overrides. */
export interface IDocumentCriteria {
  acceptedExtensions?: string[]; // e.g. ['.jpg', '.jpeg', '.png']
  minFileSize?: number; // bytes
  maxFileSize?: number; // bytes
  minWidth?: number; // px
  minHeight?: number; // px
  maxWidth?: number; // px
  maxHeight?: number; // px
  aspectRatio?: number; // width / height; 1 === 1:1
  isImage?: boolean; // image-only criteria (dimension/ratio checks apply)
  isCroppable?: boolean; // present the crop/resize/adjust modal before upload
}

/** Admin-editable user-facing copy for an upload document component. */
export interface IDocumentText {
  instructions?: string;
  success?: string;
  aiRejection?: string;
}

/** How an upload component's Image Guidelines companion box presents its guidance. */
export enum ImageGuidelineType {
  Image = 'image',
  RichText = 'richText',
}

/**
 * Admin-configurable "Image Guidelines" companion box shown beside the crop tool.
 * Either an externally hosted image (referenced by URL) or rich text content.
 * `heading` is stored so the label can be made editable later without a data reshape.
 */
export interface IImageGuidelines {
  enabled?: boolean; // show/hide the guidance box (default ON)
  type?: ImageGuidelineType; // which guidance presentation is active
  imageUrl?: string; // when type === Image: external/S3 image reference
  richText?: string; // when type === RichText: sanitized HTML content
  heading?: string; // box heading (fixed to "Image Guidelines" in V1)
}

export interface IElementSchema {
  id: string;
  type: FormComponentType;
  inputType?: InputType; // input only
  valueType?: ValueType;
  elements?: IElementSchema[]; // panel only
  repeatable?: boolean; // panel only
  maxRepeatable?: number; // panel only
  isOrderedList?: boolean; // repeatable panel only
  name: string;
  title?: string;
  placeHolder?: string; // input, select only
  options?: IOption[]; // select, checkboxgroup, radiogroup, autocomplete, dragNdropOptionPanel only
  dataSource?: IElementDataSource; // select, checkboxgroup, radiogroup, autocomplete only
  maxLength?: number; // input only
  hasCheckAll?: boolean; // checkboxgroup only
  hasOtherOption?: boolean; // radiogroup only
  disable?: any[] | boolean; // set to true to force disable a field
  visible?: IVisibleSchema;
  allowNegativeValue?: boolean;
  validation?: IValidationSchema;
  tooltip?: string;
  instructiveLabel?: string;
  canSaveToProfile?: boolean; // profileInfo only
  canPopulateFromProfile?: boolean; // profileInfo only
  showTime?: boolean; // date picker only
  url?: string; // linkToPage only
  linkLabel?: string; // linkToPage only
  encrypted?: boolean; // SSN only
  titlePath?: string;
  maxFileSize?: number; // file upload only
  maxUploadLimit?: number; // file upload only
  documentType?: DocumentType; // file upload only
  pullFromDocBank?: boolean; //file upload only
  documentCriteria?: IDocumentCriteria; // upload only - admin overrides of validation criteria
  documentText?: IDocumentText; // upload only - editable user-facing copy
  imageGuidelines?: IImageGuidelines; // upload only - guidance companion box shown beside the crop tool
  aiReview?: boolean; // upload only - run AI appropriateness review after crop
  uploadAttemptLimit?: number; // upload only - max AI validation attempts before NEEDS REVIEW
  currencySymbol?: string;
  validationRequired?: boolean; //only Upload panel
  isEvaluationRequired?: boolean; //only Upload panel
  answer?: any; // quiz only
  populateFromProfile?: boolean;
  syncToProfile?: boolean;
  profileSyncField?: string;
  preventSearchStringSave?: boolean; // only autocomplete
  decimalPlaces?: number;
  hiddenValue?: string; // only hidden
  hiddenFieldType?: string; // only hidden
  isDeselectable?: boolean; // only radio group
  // Payment Campaign only
  campaignDates?: ICampaignDate[];
  currency?: string;
  timeZone?: string;
  stageStartDate?: string; // ISO date string - when form opens
  stageEndDate?: string; // ISO date string - when form closes
  processingFeeRate?: number;
  // Phone number only - SMS/text consent
  smsOptInEnabled?: boolean;
  smsDisclosureId?: string;
  smsDisclosureText?: string;
}

export interface IValidationSchema {
  type?: string; // keyof yup.LocaleObject; <- this doesn't work..
  rules: IValidationRuleSchema[];
}

// TODO: support nested structure once we need it
export interface IVisibleSchema {
  fieldName?: string;
  operator?: Operator;
  value?: any;
  label?: string | string[];
  lookup?: LookupType;
  or?: OrCondition[]; //for nested conditions
}

export enum ValidationRuleType {
  REQUIRED = 'required',
  DEFAULT = 'default',
  // TODO Needs to implement
  MIN = 'min',
  MAX = 'max',
  EMAIL = 'email',
  YEAR = 'year',
  INTEGER = 'integer',
  POSITIVE = 'positive',
  NULLABLE = 'nullable',
  RTE_REQUIRED = 'rteRequired',
  RTE_MAX_WORD_COUNT = 'rteMaxWordCount',
  RTE_MIN_WORD_COUNT = 'rteMinWordCount',
  VALIDATOR_FN = 'test',
  /** Restricts to letters, numbers, spaces, and common address punctuation (no decorative/symbol characters). */
  SAFE_ADDRESS_TEXT = 'safeAddressText',
}

export interface IValidationRuleSchema {
  type: ValidationRuleType;
  params?: any[];
}

export interface IPageSchema {
  type: string;
  title?: string;
  elements: IElementSchema[];
}

export interface ISectionSchema {
  type: string;
  title?: string;
  pages: IPageSchema[];
}

export interface IFormSchema {
  sections: ISectionSchema[];
}

export interface IFormDataSchema {
  data: IFormData;
  encryptFields?: string[];
}

export interface IFormData {
  [key: string]: any;
}

export type IFormPayload = IFormDataSchema;

export interface IOption {
  label: string;
  value: number | boolean | string;
}

export interface IElementDataSource {
  url: string;
  valueName: string;
  titleName: string;
  sourceField?: string;
  lookup?: LookupType;
  transformTitle?: (value: string, data?: IMasterTableData) => string;
}

export interface IMemo {
  shouldValidate?: boolean;
}

export interface ICorrectionComment {
  key: string;
  comment: string;
  // Short summary for the applicant (from AI verification).
  summary?: string;
  // What’s wrong with the document.
  issues?: string[];
  // Clear steps the applicant must take.
  requiredActions?: string[];
}

//-------------------------------------------
// Form Errors
//-------------------------------------------

export interface ISectionFormError {
  pages: FieldErrors<Record<string, any>>[];
}
export interface IFormErrors {
  sections: ISectionFormError[];
}

//-------------------------------------------
// Form Status
//-------------------------------------------

// TODO: finalize the form status
export enum FormStatus {
  Completed = 'completed',
  NotStarted = 'notStarted',
  InProgress = 'inProgress',
  NeedsCorrection = 'needsCorrection',
  Declined = 'declined',
  Flagged = 'flagged',
  Pending = 'pending',
  Resubmitted = 'resubmitted',
}

export enum RequestStatus {
  PENDING = 'requested',
  ACCEPTED = 'accepted',
  DECLINED = 'rejected',
}

//TODO: this is for both volunteer application (accepted/declined) and certification (certified/expired/not_certified), should break this out one tday
export enum CertificateStatus {
  Accepted = 'accepted',
  Declined = 'declined',
  Certified = 'certified',
  NotCertified = 'notCertified',
  Expired = 'expired',
  NotAvailable = 'notAvailable',
  PendingReview = 'pendingReview',
  Revoked = 'revoked',
}

export enum BackgroundCheckStatus {
  Passed = 'passed',
  NotPassed = 'notPassed',
  NotAvailable = 'notAvailable',
}

export enum HousingStatusType {
  OnCampus = 'onCampus',
  OffCampus = 'offCampus',
  LivingWithParentsOrRelatives = 'livingWithParentsOrRelatives',
}
//-------------------------------------------
// Quiz Form Status
//-------------------------------------------

// matching the name with BE
export enum QuizStatus {
  PASSED = 'passed',
  NOT_PASSED = 'notPassed',
  NOT_STARTED = 'notStarted',
  NOT_AVAILABLE = 'notAvailable',
}

export interface IFormDictionary {
  roleName: string;
  packetUrl?: string;
  videoUrl?: string;
  nda: {
    forms: string[];
    agreements?: string[];
    title: string;
    instructiveLabel: string;
    ndaListTitle: string;
    ndaCheckboxTitle: string;
  };
}

export enum RecommenderType {
  Clergy = 'clergy',
  Coach = 'coach',
  Colleague = 'colleague',
  Counselor = 'counselor',
  ElectedOfficial = 'electedOfficial',
  Manager = 'manager',
  Mentor = 'mentor',
  Principal = 'principal',
  Professor = 'professor',
  ResearchThesisAdvisor = 'researchThesisAdvisor',
  SchoolAdministrator = 'schoolAdministrator',
  Supervisor = 'supervisor',
  Teacher = 'teacher',
  Tutor = 'tutor',
}

export enum EnrollmentVerificationType {
  /**
   * @deprecated PME-2382 — no longer selectable. Kept so submissions and stage form schemas saved
   * before the change still resolve to a label. See DEPRECATED_ENROLLMENT_VERIFICATION_TYPES.
   */
  HSFForm = 'HSFForm',
  NationalStudentClearingHouse = 'nationalStudentClearingHouse',
  InstitutionalVerificationForm = 'institutionalVerificationForm',
}

/** Form types that are no longer offered but can still appear in saved data (PME-2382). */
export const DEPRECATED_ENROLLMENT_VERIFICATION_TYPES: EnrollmentVerificationType[] = [
  EnrollmentVerificationType.HSFForm,
];

export const isDeprecatedEnrollmentVerificationType = (value?: string | null): boolean =>
  DEPRECATED_ENROLLMENT_VERIFICATION_TYPES.includes(value as EnrollmentVerificationType);

export enum CurrentEnrollmentStatusType {
  FullTime = 'fullTime',
  ThreeQuarterTime = 'threeQuarterTime',
  HalfTime = 'halfTime',
  LessThanHalfTime = 'lessThanHalfTime',
  Enrolled = 'enrolled',
  LeaveOfAbsence = 'leaveOfAbsence',
  Withdrawn = 'withdrawn',
  Deceased = 'deceased',
}

export enum AcademicTerm {
  Fall = 'Fall',
  Winter = 'Winter',
  Spring = 'Spring',
  Summer = 'Summer',
}

export enum GPAScaleType {
  Four = '4.0',
  Five = '5.0',
  Six = '6.0',
  Seven = '7.0',
  Ten = '10.0',
  Hundread = '100.0',
  Written = 'Written Evaluation',
  Other = 'Other Scale',
}

export enum GPAWeightType {
  Weighted = 'Weighted',
  Unweighted = 'Unweighted',
  NotApplicable = 'Not Applicable',
}

export interface ICurrentPosition {
  sectionIndex: number;
  pageIndex: number;
}
export interface IPageState {
  validated: boolean;
  showError: boolean;
}

export interface ISectionState {
  validated: boolean;
  expanded: boolean;
  pages: IPageState[];
}
export interface IFormState {
  currentPosition: ICurrentPosition;
  sections: ISectionState[];
  hiddenFields?: IHiddenFields;
}
export interface IStatusExplanations {
  acceptExplanation?: string;
  declineExplanation?: string;
}

export interface IFormMemo {
  formStatus?: FormStatus;
  nda?: boolean | null;
  quizCount?: number;
  quizStatus?: QuizStatus;
}

//-------------------------------------------
// Hidden Fields
//-------------------------------------------

export interface IHiddenFields {
  [key: string]: boolean;
}

export enum HiddenFieldType {
  DEFAULT = 'default',
  EVENT_ROLE = 'eventRole',
  USER_ROLE = 'userRole',
}

//-------------------------------------------
// Form Builder
//-------------------------------------------

export interface IComponentSettings {
  name: string;
  title?: string;
  options?: IOption[];
  dataSource?: IElementDataSource;
  visible?: IVisibleSchema;
  allowNegativeValue?: boolean;
  repeatable?: boolean;
  isOrderedList?: boolean;
  valueType?: ValueType;
  hasCheckAll?: boolean;
  hasOtherOption?: boolean;
  validation?: IValidationSchema;
  tooltip?: string;
  isMultilineText?: boolean;
  instructiveLabel?: string;
  valueSettingOptions?: IOption[];
  canSaveToProfile?: boolean;
  canPopulateFromProfile?: boolean;
  linkLabel?: string;
  url?: string;
  maxRepeatable?: number;
  documentType?: DocumentType;
  documentCriteria?: IDocumentCriteria;
  documentText?: IDocumentText;
  imageGuidelines?: IImageGuidelines;
  aiReview?: boolean;
  uploadAttemptLimit?: number;
  validationRequired?: boolean;
  isEvaluationRequired?: boolean;
  syncToProfile?: boolean;
  populateFromProfile?: boolean;
  profileSyncField?: string;
  decimalPlaces?: number;
  hiddenValue?: string;
  hiddenFieldType?: string;
  preventSearchStringSave?: boolean;
  // Payment Campaign only
  campaignDates?: ICampaignDate[];
  currency?: string;
  timeZone?: string;
  stageStartDate?: string; // ISO date string - when form opens
  stageEndDate?: string; // ISO date string - when form closes
  processingFeeRate?: number;
  // Phone number only - SMS/text consent
  smsOptInEnabled?: boolean;
  smsDisclosureId?: string;
  smsDisclosureText?: string;
}

//-------------------------------------------
// Yup Validation
//-------------------------------------------
// Borrowed from https://github.com/react-hook-form/react-hook-form/blob/master/src/types.ts
type Ref = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | any;
export type FieldValues = Record<string, any>;
export type ValidateResult = string | boolean | undefined;
export type MultipleFieldErrors = Record<string, ValidateResult>;

export interface FieldError {
  type: string;
  ref?: Ref;
  types?: MultipleFieldErrors;
  message?: string;
  isManual?: boolean;
}

export type NestDataObject<FormValues> = {
  [Key in keyof FormValues]?: FormValues[Key] extends Array<infer U>
    ? 0 extends 1 & U
      ? any
      : unknown extends U
        ? FieldError[]
        : object extends U
          ? FieldError[]
          : U extends Date
            ? FieldError[]
            : U extends object
              ? FieldErrors<U>[]
              : FieldError[]
    : 0 extends 1 & FormValues[Key]
      ? any
      : unknown extends FormValues[Key]
        ? FieldError
        : object extends FormValues[Key]
          ? FieldError
          : FormValues[Key] extends Date
            ? FieldError
            : FormValues[Key] extends object
              ? FieldErrors<FormValues[Key]>
              : FieldError;
};

export type SchemaValidateOptions = Partial<{
  strict: boolean;
  abortEarly: boolean;
  stripUnknown: boolean;
  recursive: boolean;
  context: object;
}>;

export type FieldErrors<FormValues> = NestDataObject<FormValues>;

export interface SchemaValidationResult<FormValues> {
  errors: FieldErrors<FormValues>;
  values: FieldValues;
}
export interface YupValidationError {
  inner: { path: string; message: string; type: string }[];
  path: string;
  message: string;
  type: string;
}

export interface Schema<Data> {
  validate(value: FieldValues, options?: SchemaValidateOptions): Promise<Data>;
}

export type PathOption = {
  labelPath: string;
  valuePath: string;
};

export type TreeNodeOption = {
  title: string;
  value?: string;
  key?: string;
  children?: TreeNodeOption[];
  selectable: boolean;
  disabled: boolean;
};
